import { getToken, clearToken } from "./auth";

const BASE = import.meta.env.VITE_API_BASE ?? "";

export type OrderState =
  | "received"
  | "mapped"
  | "generating_assets"
  | "assets_ready"
  | "quality_checking"
  | "awaiting_affinity"
  | "print_files_uploaded"
  | "awaiting_review"
  | "ready_to_order"
  | "mastering_uploaded"
  | "title_live"
  | "order_uploaded"
  | "order_confirmed"
  | "shipped"
  | "on_hold"
  | "needs_attention"
  | "cancelled"
  | "rejected_in_review";

export type MasteringStatus = "not_uploaded" | "uploaded" | "failed" | "accepted";
export type OrderFtpStatus = "not_uploaded" | "uploaded" | "failed" | "confirmed";

export interface OrderListRow {
  id: string;
  order_number: string;
  shopify_order_id: string | null;
  shopify_order_number: string | null;
  shop: string;
  customer_email: string | null;
  state: OrderState;
  mastering_status: MasteringStatus;
  order_status: OrderFtpStatus;
  hidden: boolean;
  created_at: string;
  state_changed_at: string;
  /** Mindestens eine Position mit Add-on „Outfit vom Foto“. */
  has_photo_outfit: boolean;
}

export interface OrderListResponse {
  orders: OrderListRow[];
  counts_by_state: { state: OrderState; count: number }[];
  hidden_count: number;
  limit: number;
  offset: number;
}

export interface OrderItem {
  id: string;
  order_id: string;
  book_id: string;
  title_number: string;
  child_name: string;
  gender: "m" | "f" | "d";
  hair_color: string | null;
  eye_color: string | null;
  skin_tone: string | null;
  dedication: string | null;
  preview_character_url: string | null;
  preview_session_id: string | null;
  outfit_description: string | null;
  /** „book“ = Buch-Outfit, „photo“ = Add-on „Outfit vom Foto“. */
  outfit_choice: "book" | "photo";
  /** Nur bei „photo“: Kleidung, vom Originalfoto gelesen (kleidet das Sheet ein). */
  photo_outfit_description: string | null;
  /**
   * Kleidungs-Check auf dem Foto: null = noch nicht geprüft, true = Foto
   * unbrauchbar → Standard-Outfit (Outfit-Szenen entfallen), false = Foto-Outfit.
   */
  photo_outfit_fallback: boolean | null;
  photo_outfit_fallback_reason: string | null;
  /** Sheet-Freigabe (nur „photo“): erst danach laufen die Szenen. */
  character_sheet_approved_at: string | null;
  character_sheet_review_sent_at: string | null;
  char_identity: string | null;
  copies: number;
  render_dir: string | null;
  fields_csv_path: string | null;
  cover_pdf_path: string | null;
  bookblock_pdf_path: string | null;
  /** > 0: die PDFs oben stammen aus einem Platzhalter-Render (nicht freigebbar). */
  pdf_placeholder_scenes: number;
  line_item_properties: unknown;
  created_at: string;
}

export interface TimelineRow {
  id: number;
  order_id: string;
  ts: string;
  actor: "system" | "founder" | "printer" | "shopify";
  event: string;
  data: Record<string, unknown> | null;
}

export type ImageBatchState =
  | "submitted"
  | "in_progress"
  | "completed"
  | "failed"
  | "expired"
  | "cancelled";

/**
 * The OpenAI batch a scene was submitted in. Every automatic run batches its
 * scenes, so a scene can sit in `running` for hours — this is what tells the
 * operator that the wait is normal and not a hung job.
 *
 * `null` on a job means it is NOT part of a batch: a character sheet, a Gemini
 * scene, or a scene that was regenerated by hand (which detaches it from its
 * batch). Those run on the live API.
 */
export interface GenerationJobBatch {
  id: string;
  batch_id: string;
  state: ImageBatchState;
  request_count: number;
  succeeded_count: number;
  failed_count: number;
  last_error: string | null;
  submitted_at: string;
  last_polled_at: string | null;
  finished_at: string | null;
  /** Set once the results have been written back. null = still outstanding. */
  processed_at: string | null;
}

export interface GenerationJob {
  id: string;
  order_item_id: string;
  kind: "character_angle" | "scene" | "cover" | "learn_page";
  index: number | null;
  prompt: string | null;
  output_path: string | null;
  output_bytes: number | null;
  provider: "openai" | "gemini" | null;
  state: "pending" | "running" | "succeeded" | "failed" | "skipped";
  attempts: number;
  last_error: string | null;
  started_at: string | null;
  finished_at: string | null;
  batch: GenerationJobBatch | null;
}

export type IndesignJobState =
  | "pending"
  | "claimed"
  | "rendering"
  | "completed"
  | "failed";

export interface IndesignJob {
  id: string;
  order_item_id: string;
  state: IndesignJobState;
  claimed_by: string | null;
  /** Leere Szenen-Spalten der CSV, die dieser Render bekommen hat. */
  placeholder_scenes: number;
  claimed_at: string | null;
  heartbeat_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  cover_pdf_path: string | null;
  bookblock_pdf_path: string | null;
  cover_pdf_bytes: number | null;
  bookblock_pdf_bytes: number | null;
  attempts: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export type QualityCheckState =
  | "pending"
  | "checking"
  | "approved"
  | "needs_rework"
  | "failed";

export interface QualityCheckSingle {
  pass: boolean;
  reason?: string;
}

export interface QualityCheckResultJson {
  pass: boolean;
  checks: {
    likeness: QualityCheckSingle;
    outfit: QualityCheckSingle;
    positioning: QualityCheckSingle;
    lighting_anim: QualityCheckSingle;
    ai_bugs: QualityCheckSingle;
  };
  parseError?: string;
  rawText?: string;
}

export interface QualityCheckJob {
  id: string;
  order_item_id: string;
  generation_job_id: string;
  scene_index: number;
  state: QualityCheckState;
  attempts: number;
  auto_regen_attempts: number;
  check_result: QualityCheckResultJson | null;
  last_error: string | null;
  manual_override: boolean;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One scene of the book an item was ordered from — the full catalog, disabled
 * scenes included. `enabled` only says whether the scene is queued
 * automatically at order intake; every scene can be generated by hand.
 */
export interface BookSceneRef {
  id: string;
  book_id: string;
  index: number;
  name: string;
  csv_column: string;
  kind: "generated" | "fixed";
  scene_emotion: string | null;
  order_index: number;
  enabled: boolean;
  /** Exklusive Szene von „Outfit vom Foto“. */
  photo_outfit_only: boolean;
}

export interface OrderDetail {
  order: {
    id: string;
    order_number: string;
    shopify_order_id: string | null;
    shopify_order_number: string | null;
    shop: string;
    customer_email: string | null;
    state: OrderState;
    mastering_status: MasteringStatus;
    order_status: OrderFtpStatus;
    hidden: boolean;
    // Aus der Versandbestätigung (SC-XML) der Druckerei; null bis sie da ist.
    carrier: string | null;
    tracking_number: string | null;
    shipped_at: string | null;
    shipping_address: Record<string, unknown> | null;
    raw_payload: Record<string, unknown> | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
    state_changed_at: string;
    // Freigabe vor dem Drucker-Upload. Ohne review_approved_at verweigert der
    // Uploader die Übertragung — siehe services/review-approval.ts.
    review_requested_at: string | null;
    review_approved_at: string | null;
    review_approved_by: string | null;
    review_rejected_at: string | null;
    review_reject_reason: string | null;
    // Rückschreiben der Bestellnummer an die Shopify-Order (Tag + Notiz),
    // passiert nach dem Druckerei-Upload.
    shopify_writeback_at: string | null;
    shopify_writeback_error: string | null;
    // Shopify-Bestellung als versendet markiert (inkl. Sendungsnummer); der
    // Schritt, der die Versandbestätigung an den Kunden auslöst.
    shopify_fulfilled_at: string | null;
    shopify_fulfillment_error: string | null;
    // Manuelle Versandmail an den Kunden gesendet (sperrt ein zweites Senden).
    customer_shipping_mail_sent_at: string | null;
    customer_return_mail_sent_at: string | null;
  };
  items: OrderItem[];
  timeline: TimelineRow[];
  generation_jobs: GenerationJob[];
  indesign_jobs: IndesignJob[];
  quality_check_jobs: QualityCheckJob[];
  book_scenes: BookSceneRef[];
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  return res;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authedFetch(path);
  if (!res.ok) throw new Error(`request failed: ${res.status}`);
  return res.json();
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await authedFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`request failed: ${res.status} ${text}`);
  }
  return res.json();
}

export interface TestOrderInput {
  book_id: string;
  /** Vorhandener Upload — Foto und Vorschau kommen daraus. */
  uniqid: string;
  child_name?: string;
  gender?: "Junge" | "Mädchen";
  eye_color?: string;
  binding: "Softcover" | "Hardcover";
  dedication?: string;
  matt: boolean;
  /** Add-on „Outfit vom Foto“ — die Bestellung geht dann erst auf on_hold. */
  outfit_photo: boolean;
  /** „Manuell starten“ — Bestellung startet pausiert, nichts läuft automatisch. */
  manual: boolean;
  email?: string;
}

export const ordersApi = {
  /** Legt eine Bestellung an, als käme sie von Shopify (gleicher Eingang wie der Webhook). */
  createTestOrder: (input: TestOrderInput) =>
    postJson<{ order_id: string; order_number: string; shopify_order_number: string; raw_photo_available: boolean }>(
      "/api/orders/test-order",
      input,
    ),
  list: (opts?: {
    state?: OrderState;
    hidden?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (opts?.state) params.set("state", opts.state);
    if (opts?.hidden) params.set("hidden", "1");
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return getJson<OrderListResponse>(`/api/orders${qs ? `?${qs}` : ""}`);
  },
  setHidden: (id: string, hidden: boolean) =>
    postJson<{ ok: boolean; hidden: boolean }>(`/api/orders/${id}/hidden`, {
      hidden,
    }),
  // Pure status change — no follow-on flows (see POST /:id/state on the server).
  setState: (id: string, state: OrderState) =>
    postJson<{ orderId: string; from: OrderState; to: OrderState; event: string }>(
      `/api/orders/${id}/state`,
      { state },
    ),
  get: (id: string) => getJson<OrderDetail>(`/api/orders/${id}`),
  transition: (
    id: string,
    body: { event: string; force_to?: OrderState; data?: Record<string, unknown> },
  ) =>
    postJson<{ orderId: string; from: OrderState; to: OrderState; event: string }>(
      `/api/orders/${id}/transition`,
      body,
    ),
  addNote: (id: string, text: string) =>
    postJson<{ ok: boolean }>(`/api/orders/${id}/note`, { text }),
  retryGenerationJob: (orderId: string, jobId: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/generation-jobs/${jobId}/retry`,
      {},
    ),
  // Re-run the character-sheet job from the original child photo with the
  // current prompt. Scenes stay untouched.
  regenerateCharacterSheet: (orderId: string, jobId: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/generation-jobs/${jobId}/regenerate`,
      {},
    ),
  // On-hold orders only: generate just the character sheet so it can be
  // checked before the hold is lifted. Scenes wait for „Hold aufheben“.
  generateCharacterSheet: (orderId: string) =>
    postJson<{ ok: boolean; enqueued: number }>(
      `/api/orders/${orderId}/character-sheet/generate`,
      {},
    ),
  // „Outfit vom Foto“: Sheet freigeben (startet die Szenen) bzw. die
  // Freigabe-Mail erneut senden — dieselben Aktionen wie der Link in der Mail.
  characterSheetAction: (
    orderId: string,
    itemId: string,
    action: "approve" | "review-mail",
  ) =>
    postJson<{ ok: boolean; resumed?: boolean; waitingFor?: number; sent?: boolean }>(
      `/api/orders/${orderId}/items/${itemId}/character-sheet/${action}`,
      {},
    ),
  editJobImage: (orderId: string, jobId: string, prompt: string) =>
    postJson<{ ok: boolean; bytes: number }>(
      `/api/orders/${orderId}/generation-jobs/${jobId}/edit-image`,
      { prompt },
    ),
  retryIndesignJob: (orderId: string, jobId: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/indesign-jobs/${jobId}/retry`,
      {},
    ),
  // InDesign mit dem rendern, was da ist — fehlende Szenen = Vorlagen-Platzhalter.
  startIndesignPlaceholder: (orderId: string) =>
    postJson<{ ok: boolean; queued: number; missing_scenes: number }>(
      `/api/orders/${orderId}/indesign/start-placeholder`,
      {},
    ),
  startIndesign: (orderId: string) =>
    postJson<{ ok: boolean; inserted: number }>(
      `/api/orders/${orderId}/indesign/start`,
      {},
    ),
  buildPrintPackage: (id: string) =>
    postJson<{ ok: boolean; root: string }>(
      `/api/orders/${id}/print-package/build`,
      {},
    ),
  approveReview: (id: string) =>
    postJson<{ ok: boolean }>(`/api/orders/${id}/review/approve`, {}),
  rejectReview: (id: string, reason: string) =>
    postJson<{ ok: boolean }>(`/api/orders/${id}/review/reject`, { reason }),
  sendShippingMail: (
    id: string,
    variant: "shipping" | "return",
    kind: "test" | "send",
  ) =>
    postJson<{ ok: boolean; to: string }>(
      `/api/orders/${id}/shipping-mail/${variant}/${kind}`,
      {},
    ).catch((e: Error) => {
      // Server liefert {"error": "..."} – dem Nutzer nur den Text zeigen.
      const m = /\{.*\}$/s.exec(e.message);
      if (m) {
        try {
          throw new Error((JSON.parse(m[0]) as { error: string }).error);
        } catch (inner) {
          if (inner instanceof SyntaxError) throw e;
          throw inner;
        }
      }
      throw e;
    }),
  resendReview: (id: string) =>
    postJson<{ ok: boolean; sent: boolean }>(`/api/orders/${id}/review/resend`, {}),
  uploadMastering: (id: string) =>
    postJson<{
      ok: boolean;
      remote_dir: string;
      files: { name: string; remotePath: string; bytes: number }[];
    }>(`/api/orders/${id}/mastering/upload`, {}),
  uploadOrder: (id: string) =>
    postJson<{
      ok: boolean;
      remote_dir: string;
      files: { name: string; remotePath: string; bytes: number }[];
    }>(`/api/orders/${id}/order/upload`, {}),
  redo: (id: string) =>
    postJson<{ ok: boolean; cleared_items: number }>(
      `/api/orders/${id}/redo`,
      {},
    ),
  regenerateScenes: (id: string) =>
    postJson<{ ok: boolean; items: number }>(
      `/api/orders/${id}/scenes/regenerate`,
      {},
    ),
  // Manually (re-)queue individual scenes of one item — works for scenes that
  // are switched off for automatic generation too.
  generateScenes: (orderId: string, itemId: string, indices: number[]) =>
    postJson<{
      ok: boolean;
      queued: number[];
      fixed: number[];
      skipped: { index: number; reason: string }[];
    }>(`/api/orders/${orderId}/items/${itemId}/scenes/generate`, { indices }),
  setOutfitDescription: (orderId: string, itemId: string, text: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/items/${itemId}/outfit-description`,
      { outfit_description: text },
    ),
  setPhotoOutfitDescription: (orderId: string, itemId: string, text: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/items/${itemId}/photo-outfit-description`,
      { photo_outfit_description: text },
    ),
  setCharIdentity: (orderId: string, itemId: string, text: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/items/${itemId}/char-identity`,
      { char_identity: text },
    ),
  setDedication: (orderId: string, itemId: string, text: string) =>
    postJson<{ ok: boolean; csv_rebuilt: boolean }>(
      `/api/orders/${orderId}/items/${itemId}/dedication`,
      { dedication: text },
    ),
  setChildName: (orderId: string, itemId: string, childName: string) =>
    postJson<{ ok: boolean; changed: boolean; csv_rebuilt: boolean }>(
      `/api/orders/${orderId}/items/${itemId}/child-name`,
      { child_name: childName },
    ),
  setShippingAddress: (orderId: string, address: Record<string, string>) =>
    postJson<{ ok: boolean; changed: string[]; already_sent_to_printer: boolean }>(
      `/api/orders/${orderId}/shipping-address`,
      address,
    ),
  reextractDescriptions: (
    orderId: string,
    itemId: string,
    target: "outfit" | "identity" | "both" = "both",
  ) =>
    postJson<{
      ok: boolean;
      outfit_description: string | null;
      char_identity: string | null;
      errors: string[];
    }>(`/api/orders/${orderId}/items/${itemId}/reextract-descriptions`, { target }),
  recheckQuality: (orderId: string, qcJobId: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/quality-check-jobs/${qcJobId}/recheck`,
      {},
    ),
  approveQuality: (orderId: string, qcJobId: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/quality-check-jobs/${qcJobId}/approve`,
      {},
    ),
  regenerateSceneForQuality: (orderId: string, qcJobId: string) =>
    postJson<{ ok: boolean }>(
      `/api/orders/${orderId}/quality-check-jobs/${qcJobId}/regenerate`,
      {},
    ),
};

/** Build PDF URL for a downloaded InDesign render. */
export function pdfUrl(
  orderId: string,
  itemId: string,
  kind: "cover" | "bookblock",
): string {
  return `${BASE}/api/orders/${orderId}/items/${itemId}/pdf/${kind}`;
}

/**
 * Build the print-package ZIP download URL. The endpoint rebuilds the package
 * on the fly. `xmlOnly` limits the archive to the order/mastering XML files.
 */
export function printPackageDownloadUrl(id: string, xmlOnly = false): string {
  return `${BASE}/api/orders/${id}/print-package/download${xmlOnly ? "?xml=1" : ""}`;
}

/** Build asset URL (server validates authentication on the route itself). */
export function assetUrl(
  orderId: string,
  itemId: string,
  kind: "angles" | "scenes",
  filename: string,
): string {
  return `${BASE}/api/orders/${orderId}/items/${itemId}/assets/${kind}/${encodeURIComponent(filename)}`;
}

/** Fetch an asset as an Object URL (since asset routes require auth). */
export async function fetchAssetBlobUrl(url: string): Promise<string> {
  const token = getToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // Surface the server's JSON error message when there is one (e.g. the
    // print-package builder rejecting because title dimensions are missing).
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body && typeof body.error === "string") message = body.error;
    } catch {
      /* non-JSON body — fall back to status code */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Trigger a browser download for an authenticated URL. We can't use a plain
 * <a download> because the route requires a bearer header — so fetch the
 * blob with auth, hand it to the browser as an object URL, then revoke.
 */
export async function downloadAuthed(url: string, suggestedFilename: string): Promise<void> {
  const blobUrl = await fetchAssetBlobUrl(url);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = suggestedFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a beat to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
}

export const STATE_LABELS: Record<OrderState, string> = {
  received: "Empfangen",
  mapped: "Gemappt",
  generating_assets: "Assets werden generiert",
  assets_ready: "Assets bereit",
  quality_checking: "Quality-Check läuft",
  awaiting_affinity: "Warte auf Affinity",
  print_files_uploaded: "Print-Files hochgeladen",
  awaiting_review: "Review & Accept",
  ready_to_order: "Bereit zum Versand",
  mastering_uploaded: "Mastering hochgeladen",
  title_live: "Titel live beim Drucker",
  order_uploaded: "Order hochgeladen",
  order_confirmed: "Order bestätigt",
  shipped: "Versendet",
  on_hold: "Pausiert",
  needs_attention: "Aufmerksamkeit nötig",
  cancelled: "Storniert",
  rejected_in_review: "Im Review abgelehnt",
};

export const MASTERING_STATUS_LABELS: Record<MasteringStatus, string> = {
  not_uploaded: "Nicht hochgeladen",
  uploaded: "Mastering hochgeladen",
  failed: "Mastering fehlgeschlagen",
  accepted: "Mastering abgenommen",
};

export const ORDER_STATUS_LABELS: Record<OrderFtpStatus, string> = {
  not_uploaded: "Nicht hochgeladen",
  uploaded: "Order hochgeladen",
  failed: "Order abgelehnt",
  confirmed: "Order bestätigt",
};

export const STATE_ORDER: OrderState[] = [
  "received",
  "mapped",
  "generating_assets",
  "assets_ready",
  "quality_checking",
  "awaiting_affinity",
  "print_files_uploaded",
  "awaiting_review",
  "ready_to_order",
  "mastering_uploaded",
  "title_live",
  "order_uploaded",
  "order_confirmed",
  "shipped",
  "on_hold",
  "needs_attention",
  "rejected_in_review",
  "cancelled",
];
